﻿import View from "./view.js";
import { takeoutLabel } from "../orderTypeLabel.js";
import { esc } from "../escape.js";
import { formatMoney, currencySymbol } from "../currency.js";
import { extractInclusiveTax, taxLabel, splitTaxableGross, exemptLabel } from "../inclusiveTax.js";
import { computeStreak, formatPhone, stripNonDigits, stripNonNameChars, formatBirthday, isBirthdayToday } from "../loyalty.js";
import { tipAmountFor, tipFromPercent } from "../tips.js";

class OrderCheckOutView extends View {
  _parentElement = document.querySelector(".modal-parent");
  _subtotal;
  _adjResult;
  _totalPrice;
  _canUseLoyalty = false; // stamped by the controller (use_loyalty capability)
  _customerPayment;
  _customerChange;
  _orderType = 'dine-in';
  _paymentMethod = 'cash';
  // Tables (2026-10-22). _tables: [{ id, name }] for the "Which table?" picker
  // (shown when table service is on and the order is dine in); _table: the
  // pick ({ id, name } or { name } typed in, null = no table). _tabPay is set
  // when this checkout pays an open tab ({ tableName, split }).
  _tables = [];
  _table = null;
  _tabPay = null;
  // Tip for this checkout (reset by the controller on every new checkout).
  // { mode: 'none' | 'percent' | 'custom' | 'qr', percent?, amount? }
  _tip = { mode: 'none' };
  _tipAsking = false;   // waiting for the customer to pick on the customer display
  _tipCustomOpen = false;
  // Checkout layout "Option A" (2026-10-15): Discount / Loyalty / Tip live in a
  // tabbed card under the order summary so the payment column never scrolls.
  // Reset to 'promo' by the controller on every new checkout.
  _extrasTab = 'promo';
  _promoCode = null;   // last promo/loyalty passed to _refreshAdjustments, for the tab badges
  _loyalty = null;

  // ── Tip ───────────────────────────────────────────────────────────────────────

  tipAmount() {
    return tipAmountFor(this._tip, this._totalPrice);
  }

  // What the customer actually pays: the sale total plus the tip. The sale
  // total itself (_totalPrice) never includes the tip.
  chargeTotal() {
    return (Number(this._totalPrice) || 0) + this.tipAmount();
  }

  _tipsOn() {
    return this._data?.settings?.tipsEnabled === true;
  }

  _tipSectionMarkup() {
    if (!this._tipsOn()) return '';
    const presets = this._data.settings.tipPresets ?? [];
    const tip = this._tip;
    const amount = this.tipAmount();
    const off = this._tipAsking ? ' disabled' : '';
    const opt = (active, attrs, main, sub = '') => `
      <button type="button" class="co-tip-opt${active ? ' co-tip-opt--active' : ''}" ${attrs}${off}>
        <span class="co-tip-opt-main">${main}</span>${sub ? `<span class="co-tip-opt-sub">${sub}</span>` : ''}
      </button>`;

    // The percentages are the tiles; No tip + Custom sit underneath as one slim
    // two-part switch (Kelly's pick, "D3", 2026-10-15). No tip is the default,
    // so it gets a quiet check rather than the solid green of a chosen tile.
    const options = presets.map((p) => opt(
      tip.mode === 'percent' && Number(tip.percent) === Number(p) && !this._tipCustomOpen,
      `data-tip-mode="percent" data-tip-percent="${Number(p)}"`,
      `${Number(p)}%`, formatMoney(tipFromPercent(this._totalPrice, p)),
    )).join('');
    // While the custom field is open, Custom is the selection being made.
    const noOn = (tip.mode === 'none' || tip.mode === 'qr') && !this._tipCustomOpen;
    const cuOn = tip.mode === 'custom' || this._tipCustomOpen;
    const altRow = `
      <div class="co-tip-alt">
        <button type="button" class="co-tip-alt-btn${noOn ? ' co-tip-alt-btn--active' : ''}" data-tip-mode="none"${off}>
          ${noOn ? '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12l5 5 9-10"/></svg>' : ''}No tip
        </button>
        <button type="button" class="co-tip-alt-btn co-tip-alt-btn--custom${cuOn ? ' co-tip-alt-btn--active' : ''}" data-tip-mode="custom"${off}>
          <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 20h9"/><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg>Custom${tip.mode === 'custom' ? ` <span class="co-tip-alt-amt">${formatMoney(amount)}</span>` : ''}
        </button>
      </div>`;

    const customRow = this._tipCustomOpen && !this._tipAsking ? `
      <div class="co-tip-custom">
        <div class="pos-amount-input">
          <span class="pos-amount-currency" aria-hidden="true">${currencySymbol()}</span>
          <input type="number" id="coTipCustomInput" min="0" step="0.01" placeholder="Tip amount"
            value="${tip.mode === 'custom' ? amount : ''}" />
        </div>
        <button type="button" class="btn primary" id="coTipCustomApply">Add tip</button>
      </div>` : '';

    const askRow = this._tipAsking ? `
      <div class="co-tip-waiting" role="status">
        <span class="co-tip-waiting-dot" aria-hidden="true"></span>
        <span class="co-tip-waiting-text">Waiting for the customer to choose on the display…</span>
        <button type="button" class="co-tip-link" id="coTipCancelAsk">Pick here instead</button>
      </div>` : `
      <button type="button" class="co-tip-ask" id="coTipAskBtn">
        <span class="co-tip-ask-icon" aria-hidden="true"><svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="12" rx="2"/><path d="M8 20h8M12 16v4"/></svg></span>
        <span class="co-tip-ask-text">Let the customer choose on the display</span>
        <svg class="co-tip-ask-arrow" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m9 6 6 6-6 6"/></svg>
      </button>`;

    const qrNote = tip.mode === 'qr'
      ? `<p class="co-tip-note">The customer tipped with your tip QR. Those tips go straight to your account, so they aren't recorded here.</p>`
      : '';

    // No header: the Tip tab above names the section and shows the amount.
    return `
      <div class="co-tip${this._tipAsking ? ' co-tip--asking' : ''}" id="coTipSection">
        ${presets.length ? `<div class="co-tip-options" style="--tip-presets:${presets.length}">${options}</div>` : ''}
        ${altRow}
        ${customRow}
        ${askRow}
        ${qrNote}
      </div>`;
  }

  // "Sale $11.00 + tip $1.65" under the total, only when there's a tip.
  _tipSplitText() {
    const amount = this.tipAmount();
    if (amount <= 0) return '';
    return `Sale ${formatMoney(this._totalPrice)} + tip ${formatMoney(amount)}`;
  }

  // Re-render the tip section + total after the tip (or the total) changed.
  // A changed amount voids any cash amount already entered, same as a total
  // change in _refreshAdjustments.
  refreshTip() {
    const section = this._parentElement.querySelector('#coTipSection');
    if (section) section.outerHTML = this._tipSectionMarkup();
    const totalEl = this._parentElement.querySelector('#cartTotal');
    if (totalEl) totalEl.textContent = formatMoney(this.chargeTotal());
    const barTotalEl = this._parentElement.querySelector('#coPaybarTotal');
    if (barTotalEl) barTotalEl.textContent = formatMoney(this.chargeTotal());
    const splitEl = this._parentElement.querySelector('#coTipSplit');
    if (splitEl) splitEl.textContent = this._tipSplitText();
    this._refreshExtrasBadges();
    this._resetPaymentAfterTotalChange();
    if (this._tipCustomOpen) this._parentElement.querySelector('#coTipCustomInput')?.focus();
  }

  setTip(tip) {
    this._tip = tip ?? { mode: 'none' };
    this.refreshTip();
  }

  setTipAsking(asking) {
    this._tipAsking = asking;
    if (asking) this._tipCustomOpen = false;
    this.refreshTip();
  }

  // Delegated (survives every re-render). onSelect(tip) for a cashier pick,
  // onAsk() / onCancelAsk() for the customer-display prompt.
  _addHandlerTip({ onSelect, onAsk, onCancelAsk }) {
    this._parentElement.addEventListener('click', (e) => {
      if (e.target.closest('#coTipAskBtn')) { onAsk(); return; }
      if (e.target.closest('#coTipCancelAsk')) { onCancelAsk(); return; }
      if (e.target.closest('#coTipCustomApply')) {
        const v = parseFloat(this._parentElement.querySelector('#coTipCustomInput')?.value);
        if (isNaN(v) || v < 0) return;
        this._tipCustomOpen = false;
        onSelect(v > 0 ? { mode: 'custom', amount: v } : { mode: 'none' });
        return;
      }
      const btn = e.target.closest('.co-tip-opt, .co-tip-alt-btn');
      if (!btn || btn.disabled) return;
      const mode = btn.dataset.tipMode;
      if (mode === 'custom') {
        this._tipCustomOpen = true;
        this.refreshTip();
        return;
      }
      this._tipCustomOpen = false;
      onSelect(mode === 'percent'
        ? { mode: 'percent', percent: Number(btn.dataset.tipPercent) }
        : { mode: 'none' });
    });
    this._parentElement.addEventListener('keydown', (e) => {
      if (e.target.id !== 'coTipCustomInput') return;
      if (['-', '+', 'e', 'E'].includes(e.key)) e.preventDefault();
      if (e.key === 'Enter') {
        e.preventDefault();
        this._parentElement.querySelector('#coTipCustomApply')?.click();
      }
    });
  }

  // ── Cart items markup ─────────────────────────────────────────────────────────

  _generateCartItemsMarkup(cart, loyaltyCtx = null) {
    return cart
      .map(
        (item, index) => {
          const allVariants = item.selectedVariants.map((v) => v.variantName);
          // Receipt-card row (2026-10-03): quantity badge first, name with its
          // options underneath, then price and the remove button side by side
          // so "remove" sits right next to what it removes.
          return `
          <div class="pos-cart-item cart-item-row co-line">
            <span class="co-line-qty">${item.quantity}×</span>
            <div class="pos-cart-item-info">
              <span class="pos-cart-item-name">${esc(item.itemName)}</span>
              ${allVariants.length ? `<span class="pos-cart-item-variants">${esc(allVariants.join(" · "))}</span>` : ""}
              ${this._loyaltyRewardPillMarkup(item, loyaltyCtx)}
            </div>
            <div class="pos-cart-item-right">
              <span class="pos-cart-item-price">${formatMoney(item.totalPrice)}</span>
              <button class="checkout-cart-delete-btn pos-cart-delete" data-cart-index="${index}" type="button" aria-label="Remove ${esc(item.itemName)}" title="Remove">×</button>
            </div>
          </div>`;
        },
      )
      .join("");
  }

  // Read-only "this line is comped" badge. The actual redeem/remove action now
  // lives in the Redeem modal (loyaltyCtx still gates on canRedeem/customer so
  // the badge never renders for a program-off/no-permission/unlinked cart).
  _loyaltyRewardPillMarkup(item, loyaltyCtx) {
    if (!loyaltyCtx || !loyaltyCtx.canRedeem || !loyaltyCtx.customer) return "";
    if (!loyaltyCtx.redeemedLineIds.has(item.lineId)) return "";
    return `
      <span class="loyalty-reward-pill loyalty-reward-pill--applied">
        <span class="loyalty-reward-pill-text">🎁 Free</span>
      </span>`;
  }

  // Receipt card header/footer: "N items" (by quantity) + items subtotal.
  _cartSummaryMeta(cart) {
    const count = cart.reduce((n, item) => n + (Number(item.quantity) || 0), 0);
    const subtotal = cart.reduce((sum, item) => sum + (Number(item.totalPrice) || 0), 0);
    return { countText: `${count} item${count === 1 ? "" : "s"}`, subtotalText: formatMoney(subtotal) };
  }

  _refreshCartItems(cart, loyaltyCtx = null) {
    const el = this._parentElement.querySelector("#cartItems");
    if (el) el.innerHTML = this._generateCartItemsMarkup(cart, loyaltyCtx);
    const { countText, subtotalText } = this._cartSummaryMeta(cart);
    const countEl = this._parentElement.querySelector("#coReceiptCount");
    if (countEl) countEl.textContent = countText;
    const subEl = this._parentElement.querySelector("#coReceiptSubtotal");
    if (subEl) subEl.textContent = subtotalText;
    const headSubEl = this._parentElement.querySelector("#coReceiptHeadSub");
    if (headSubEl) headSubEl.textContent = subtotalText;
  }

  // ── Promo code section ────────────────────────────────────────────────────────

  _generatePromoSection(promoCode, adjResult) {
    if (promoCode) {
      const promoLine = adjResult.lineItems.find((li) => li.source === "promo-code");
      const amtText = promoLine
        ? `−${formatMoney(Math.abs(promoLine.computedAmount))}`
        : promoCode.type === "percentage"
        ? `${promoCode.value}% off`
        : `${formatMoney(promoCode.value)} off`;

      return `
        <div class="promo-code-section promo-code-section--applied">
          <div class="promo-applied-info">
            <span class="promo-code-badge-sm">${esc(promoCode.code)}</span>
            <span class="promo-applied-name">${esc(promoCode.title)}</span>
            <span class="promo-applied-amount">${amtText}</span>
          </div>
          <button type="button" class="promo-remove-btn" id="removePromoBtn">Remove</button>
        </div>
      `;
    }

    return `
      <div class="promo-code-section">
        <div class="promo-input-row">
          <input type="text" id="promoCodeInput" placeholder="Enter promo code"
            autocomplete="off" spellcheck="false" style="text-transform:uppercase;" />
          <button type="button" id="applyPromoBtn">Apply</button>
        </div>
      </div>
    `;
  }

  // ── Loyalty section ───────────────────────────────────────────────────────────

  // Renders below the promo section inside #receiptAdjSection, so every
  // _refreshAdjustments re-render keeps it in sync with the current total.
  // loyalty = { customer, redemption, rewardRedemptions } from model state
  // (threaded by the controller like promoCode). Nothing renders when the
  // program is off. Once a customer is linked, this is just a summary +
  // a "Redeem" button - the actual redeem/remove flow lives in the modal
  // (showRedeemModal) so both redemption types are managed in one place.
  _generateLoyaltySection(loyalty, adjResult) {
    const settings = this._data?.settings ?? {};
    if (!settings.loyaltyEnabled || !this._canUseLoyalty) return "";
    const customer = loyalty?.customer ?? null;
    const redemption = loyalty?.redemption ?? null;
    const rewardRedemptions = loyalty?.rewardRedemptions ?? [];

    if (!customer) {
      return `
        <div class="loyalty-section" id="loyaltySection">
          <div class="pos-section-head"><span class="pos-section-emoji" aria-hidden="true">⭐</span>Loyalty</div>
          <div class="loyalty-input-row">
            <input type="tel" id="loyaltyPhoneInput" placeholder="Customer phone (optional)"
              autocomplete="off" inputmode="numeric" maxlength="15" />
            <button type="button" id="loyaltyLookupBtn">Find</button>
          </div>
        </div>
      `;
    }

    // The compact checkout badge shows the DAILY streak (the panel detail
    // shows daily, weekly, and monthly side by side).
    const streak = computeStreak(
      customer.saleDates ?? [],
      "day",
      this._data?.businessTimezone ?? undefined,
    );
    const streakBadge = streak.current > 1
      ? `<span class="loyalty-streak-badge" title="Purchase streak">🔥 ${streak.current}-day streak</span>`
      : "";

    const appliedRows = [
      redemption
        ? `<div class="loyalty-applied-row">
             <span class="loyalty-applied-text">💵 ${redemption.points} pts · −${formatMoney(redemption.value)}</span>
             <button type="button" class="loyalty-applied-remove loyalty-remove-redeem-btn" aria-label="Remove points discount">×</button>
           </div>`
        : "",
      ...rewardRedemptions.map((r) => `
           <div class="loyalty-applied-row">
             <span class="loyalty-applied-text">🎁 ${esc(r.name)}</span>
             <button type="button" class="loyalty-applied-remove loyalty-reward-remove-btn" data-cart-line-id="${esc(r.cartItemLineId)}" aria-label="Remove free item redemption">×</button>
           </div>`),
    ].filter(Boolean).join("");

    return `
      <div class="loyalty-section loyalty-section--linked" id="loyaltySection">
        <div class="pos-section-head"><span class="pos-section-emoji" aria-hidden="true">⭐</span>Loyalty</div>
        <div class="loyalty-customer-chip">
          <div class="loyalty-customer-info">
            <span class="loyalty-customer-name">${esc(customer.name || formatPhone(customer.phone))}</span>
            <span class="loyalty-customer-meta">${customer.pointsBalance} pts${streakBadge ? " · " : ""}${streakBadge}</span>
          </div>
          <button type="button" class="promo-remove-btn" id="loyaltyUnlinkBtn" title="Remove customer from this sale">×</button>
        </div>
        ${appliedRows ? `<div class="loyalty-applied-list">${appliedRows}</div>` : ""}
        <button type="button" class="loyalty-redeem-open-btn" id="loyaltyRedeemOpenBtn">
          🎁 Redeem Rewards
        </button>
      </div>
    `;
  }

  // ── Redeem modal (checkout) ──────────────────────────────────────────────────
  // Single place to see the linked customer's points/birthday/streak and
  // manage BOTH redemption types (free item + points-off); they stack, so
  // both sections are always shown together rather than picking one.

  _redeemModal = document.querySelector("#loyaltyRedeemModal");

  isRedeemModalOpen() {
    return !this._redeemModal.classList.contains("hidden");
  }

  closeRedeemModal() {
    this._redeemModal.classList.add("hidden");
  }

  // payload = { customer, streak (daily), rewards, redemptionByRewardId,
  //   reservedPoints, redeemPoints, redeemValue, redemption, finalTotal, tz }
  showRedeemModal(payload) {
    const { customer, streak, rewards, redemptionByRewardId, reservedPoints,
      redeemPoints, redeemValue, redemption, finalTotal, tz } = payload;

    const birthday = formatBirthday(customer.birthdayMonth, customer.birthdayDay);
    const birthdayToday = isBirthdayToday(customer.birthdayMonth, customer.birthdayDay, tz);

    const statChips = [
      `<div class="loyalty-redeem-stat"><span class="loyalty-redeem-stat-value">${customer.pointsBalance}</span><span class="loyalty-redeem-stat-label">Points</span></div>`,
      birthday
        ? `<div class="loyalty-redeem-stat"><span class="loyalty-redeem-stat-value">🎂 ${esc(birthday)}</span><span class="loyalty-redeem-stat-label">Birthday</span></div>`
        : "",
      streak.current > 1
        ? `<div class="loyalty-redeem-stat"><span class="loyalty-redeem-stat-value">🔥 ${streak.current}</span><span class="loyalty-redeem-stat-label">Day streak</span></div>`
        : "",
    ].filter(Boolean).join("");

    // Shows the WHOLE reward catalog (not just items already in the cart) -
    // redeeming adds the item free rather than requiring it be rung up
    // first. Rows the customer can't afford yet are greyed out, not hidden.
    const itemRowsHtml = rewards.length
      ? rewards.map((r) => {
          const redeemedLineId = redemptionByRewardId.get(r.id);
          const applied = Boolean(redeemedLineId);
          const affordable = r.pointsCost <= customer.pointsBalance - reservedPoints;
          const actionHtml = applied
            ? `<button type="button" class="loyalty-reward-remove-btn" data-cart-line-id="${esc(redeemedLineId)}">Remove</button>`
            : affordable
            ? `<button type="button" class="loyalty-reward-redeem-btn" data-reward-id="${esc(r.id)}">Redeem (${r.pointsCost} pts)</button>`
            : `<span class="loyalty-redeem-item-locked">${r.pointsCost} pts needed</span>`;
          return `
            <div class="loyalty-redeem-item-row${!applied && !affordable ? " loyalty-redeem-item-row--locked" : ""}">
              <span class="loyalty-redeem-item-name">${esc(r.itemName)}${applied ? ' <em class="loyalty-redeem-item-tag">🎁 Free</em>' : ""}</span>
              ${actionHtml}
            </div>`;
        }).join("")
      : `<p class="loyalty-redeem-empty">No rewards configured yet.</p>`;

    let pointsOffHtml;
    if (redemption) {
      pointsOffHtml = `
        <div class="loyalty-redeem-item-row">
          <span class="loyalty-redeem-item-name">Redeeming ${redemption.points} pts · −${formatMoney(redemption.value)}</span>
          <button type="button" class="btn loyalty-remove-redeem-btn">Remove</button>
        </div>`;
    } else {
      const maxByBalance = Math.floor((customer.pointsBalance - reservedPoints) / redeemPoints);
      const maxByTotal = Math.ceil(finalTotal / redeemValue);
      const max = Math.min(maxByBalance, maxByTotal);
      if (max >= 1) {
        pointsOffHtml = `
          <div class="loyalty-points-off-card" data-redeem-points="${redeemPoints}" data-redeem-value="${redeemValue}">
            <div class="loyalty-points-off-preview" id="loyaltyRedeemPreview">
              <span class="loyalty-points-off-pts">${redeemPoints} pts</span>
              <span class="loyalty-points-off-arrow">→</span>
              <span class="loyalty-points-off-value">−${formatMoney(redeemValue)}</span>
            </div>
            <div class="loyalty-points-off-controls">
              <div class="loyalty-stepper" data-max="${max}">
                <button type="button" class="loyalty-step-btn" id="loyaltyRedeemMinus" aria-label="Less">−</button>
                <span class="loyalty-step-count" id="loyaltyRedeemCount">1</span>
                <button type="button" class="loyalty-step-btn" id="loyaltyRedeemPlus" aria-label="More">+</button>
              </div>
              <button type="button" class="btn primary" id="loyaltyApplyRedeemBtn">Redeem</button>
            </div>
          </div>`;
      } else if (maxByBalance < 1) {
        const short = redeemPoints - (customer.pointsBalance - reservedPoints);
        pointsOffHtml = `<p class="loyalty-redeem-hint">${short} more point${short === 1 ? "" : "s"} to unlock ${formatMoney(redeemValue)} off</p>`;
      } else {
        pointsOffHtml = `<p class="loyalty-redeem-hint">Total is too small to redeem ${redeemPoints} pts (${formatMoney(redeemValue)} off)</p>`;
      }
    }

    this._redeemModal.querySelector("#loyaltyRedeemBody").innerHTML = `
      <button class="modal-close-btn" id="loyaltyRedeemCloseBtn" type="button">&times;</button>
      <div class="loyalty-redeem-head">
        <span class="loyalty-row-avatar loyalty-redeem-avatar" aria-hidden="true">${esc((customer.name || customer.phone).charAt(0).toUpperCase())}</span>
        <div class="loyalty-redeem-titles">
          <h2 class="edit-form-title">${esc(customer.name || formatPhone(customer.phone))}</h2>
          <p class="loyalty-detail-sub">${esc(formatPhone(customer.phone))}</p>
        </div>
      </div>
      ${birthdayToday ? `<div class="loyalty-birthday-banner">🎉 It's their birthday today!</div>` : ""}
      <div class="loyalty-redeem-stats">${statChips}</div>
      <p class="loyalty-program-section-label">Free Item</p>
      <div class="loyalty-redeem-item-list">${itemRowsHtml}</div>
      <p class="loyalty-program-section-label">Points Off</p>
      ${pointsOffHtml}
      <div class="loyalty-redeem-footer">
        <button type="button" class="btn" id="loyaltyRedeemDoneBtn">Done</button>
      </div>
    `;
    this._redeemModal.classList.remove("hidden");
    this._redeemModal.querySelector("#loyaltyRedeemCloseBtn").addEventListener("click", () => this.closeRedeemModal());
    this._redeemModal.querySelector("#loyaltyRedeemDoneBtn").addEventListener("click", () => this.closeRedeemModal());
  }

  // Swap the idle loyalty section into the inline enroll form after a lookup
  // miss. View-transient state: a later full refresh returns to idle, which is
  // fine (enrolling is a one-shot moment).
  showLoyaltyEnroll(phone) {
    const section = this._parentElement.querySelector("#loyaltySection");
    if (!section) return;
    section.innerHTML = `
      <div class="pos-section-head"><span class="pos-section-emoji" aria-hidden="true">⭐</span>Loyalty</div>
      <p class="loyalty-enroll-note">New customer · joins the rewards program</p>
      <div class="loyalty-input-row">
        <input type="tel" id="loyaltyEnrollPhone" value="${esc(phone)}" readonly />
      </div>
      <div class="loyalty-input-row">
        <input type="text" id="loyaltyEnrollName" placeholder="Name (optional)" autocomplete="off" />
        <button type="button" id="loyaltyEnrollBtn">Enroll</button>
        <button type="button" class="loyalty-cancel-btn" id="loyaltyEnrollCancelBtn">Cancel</button>
      </div>
    `;
    section.querySelector("#loyaltyEnrollName")?.focus();
  }

  // ── Adj section markup (reused by both initial render and in-place refresh) ──

  // Right-column bill (#receiptAdjSection): subtotal, then every line that
  // moves the total. Fees/taxes/manual adjustments keep their Edit / × controls
  // here; the promo and points-discount lines are read-only (they're added and
  // removed in the Discount / Loyalty tabs). Free-item reward redemptions still
  // render as a pill on their own cart line.
  _generateBillMarkup(subtotal, allAdj, adjResult, showRemoved) {
    const activeLines = adjResult.lineItems;
    const removedLines = allAdj.filter((a) => a.removed);
    const nonPromoLines = activeLines.filter(
      (li) => li.source !== "promo-code" && li.source !== "loyalty" && li.source !== "loyalty_reward",
    );
    const discountLines = activeLines.filter((li) => li.source === "promo-code" || li.source === "loyalty");

    const discountHtml = discountLines
      .map(
        (li) => `
        <div class="receipt-adj-item receipt-adj-item--readonly">
          <div class="receipt-adj-info">
            <span>${esc(li.name)}</span>
            <span class="receipt-adj-amount ${li.type}">
              ${li.computedAmount >= 0 ? "+" : "-"}${formatMoney(Math.abs(li.computedAmount))}
            </span>
          </div>
        </div>
      `,
      )
      .join("");

    const activeHtml = nonPromoLines
      .map(
        (adj) => `
        <div class="receipt-adj-item" data-adj-id="${adj.id}">
          <div class="receipt-adj-info">
            <span>${esc(adj.name)}${adj.calculation === "percentage" ? ` (${adj.appliedValue}%)` : ""}</span>
            <span class="receipt-adj-amount ${adj.type}">
              ${adj.computedAmount >= 0 ? "+" : "-"}${formatMoney(Math.abs(adj.computedAmount))}
            </span>
          </div>
          ${adj.source === 'tax' ? '' : `<div class="receipt-adj-controls">
            ${adj.source === 'manual' ? `<button class="receipt-adj-edit-btn" data-adj-id="${adj.id}" type="button">Edit</button>` : ''}
            <button class="receipt-adj-remove-btn" data-adj-id="${adj.id}" type="button">&times;</button>
          </div>`}
        </div>
      `,
      )
      .join("");

    const removedHtml = showRemoved
      ? removedLines
          .map(
            (adj) => `
          <div class="receipt-adj-item receipt-adj-item--removed">
            <div class="receipt-adj-info">
              <span>${esc(adj.name)}${adj.calculation === "percentage" ? ` (${adj.appliedValue}%)` : ""} <em>(removed)</em></span>
              <span>${formatMoney(0)}</span>
            </div>
          </div>
        `,
          )
          .join("")
      : "";

    const lines = discountHtml + activeHtml + removedHtml;
    // "Add adjustment" lives with the bill it changes (Kelly, 2026-10-15), as a
    // compact link so the laptop column still fits without scrolling.
    const addBtn = `
      <button class="receipt-add-adj-btn co-bill-add" type="button" title="Apply a tax, fee, or discount to this receipt">
        <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" aria-hidden="true"><path d="M12 5v14M5 12h14"/></svg>Add adjustment
      </button>`;
    if (!lines) return addBtn;
    return `
      <div class="cart-subtotal">
        <span>Subtotal</span>
        <span>${formatMoney(subtotal)}</span>
      </div>
      <div class="receipt-adj-list">${lines}</div>
      ${addBtn}
    `;
  }

  // Discount tab: the promo code field. ("Add adjustment" sits with the bill.)
  _generateDiscountPaneMarkup(promoCode, adjResult) {
    return this._generatePromoSection(promoCode, adjResult);
  }

  // ── Extras card (Discount / Loyalty / Tip tabs, under the order summary) ─────

  _extrasTabs() {
    const tabs = [{ key: "promo", emoji: "🏷️", label: "Discount" }];
    if (this._data?.settings?.loyaltyEnabled && this._canUseLoyalty) tabs.push({ key: "loyalty", emoji: "⭐", label: "Loyalty" });
    if (this._tipsOn()) tabs.push({ key: "tip", emoji: "💝", label: "Tip" });
    return tabs;
  }

  // What each tab has applied, shown on the tab so nothing hides behind it.
  _extrasBadge(key) {
    if (key === "promo") {
      const line = (this._adjResult?.lineItems ?? []).find((li) => li.source === "promo-code");
      return line ? `−${formatMoney(Math.abs(line.computedAmount))}` : this._promoCode ? esc(this._promoCode.code) : "";
    }
    if (key === "loyalty") {
      const c = this._loyalty?.customer;
      if (!c) return "";
      return esc((c.name || "").trim().split(/\s+/)[0] || formatPhone(c.phone));
    }
    if (key === "tip") {
      const amount = this.tipAmount();
      return amount > 0 ? `+${formatMoney(amount)}` : "";
    }
    return "";
  }

  _generateExtrasMarkup(adjResult) {
    const tabs = this._extrasTabs();
    if (!tabs.some((t) => t.key === this._extrasTab)) this._extrasTab = "promo";
    const active = this._extrasTab;
    const tabHtml = tabs
      .map((t) => {
        const badge = this._extrasBadge(t.key);
        return `
        <button type="button" class="co-extras-tab${t.key === active ? " co-extras-tab--active" : ""}" role="tab"
          id="coExtrasTab-${t.key}" data-extras-tab="${t.key}" aria-selected="${t.key === active}" aria-controls="coExtrasPane-${t.key}">
          <span class="co-extras-tab-name"><span class="pos-section-emoji" aria-hidden="true">${t.emoji}</span>${t.label}</span>
          <span class="co-extras-badge" id="coExtrasBadge-${t.key}"${badge ? "" : " hidden"}>${badge}</span>
        </button>`;
      })
      .join("");
    const pane = (key, inner) => `
      <div class="co-extras-pane" id="coExtrasPane-${key}" role="tabpanel" aria-labelledby="coExtrasTab-${key}"${key === active ? "" : " hidden"}>${inner}</div>`;
    return `
      <div class="co-extras" id="coExtras">
        <div class="co-extras-tabs" role="tablist" aria-label="Discount, loyalty and tip">${tabHtml}</div>
        ${pane("promo", `<div id="coPromoPane">${this._generateDiscountPaneMarkup(this._promoCode, adjResult)}</div>`)}
        ${tabs.some((t) => t.key === "loyalty") ? pane("loyalty", `<div id="coLoyaltyPane">${this._generateLoyaltySection(this._loyalty, adjResult)}</div>`) : ""}
        ${tabs.some((t) => t.key === "tip") ? pane("tip", this._tipSectionMarkup()) : ""}
      </div>`;
  }

  _refreshExtrasBadges() {
    for (const t of this._extrasTabs()) {
      const el = this._parentElement.querySelector(`#coExtrasBadge-${t.key}`);
      if (!el) continue;
      const badge = this._extrasBadge(t.key);
      el.innerHTML = badge;
      el.hidden = !badge;
    }
  }

  // Switch tabs in place (no re-render - that would reset an entered cash amount).
  _selectExtrasTab(key) {
    this._extrasTab = key;
    this._parentElement.querySelectorAll(".co-extras-tab").forEach((b) => {
      const on = b.dataset.extrasTab === key;
      b.classList.toggle("co-extras-tab--active", on);
      b.setAttribute("aria-selected", String(on));
    });
    this._parentElement.querySelectorAll(".co-extras-pane").forEach((p) => {
      p.hidden = p.id !== `coExtrasPane-${key}`;
    });
  }

  // ── Table picker ──────────────────────────────────────────────────────────────

  _tablePickerShown() {
    if (this._tabPay || (this._data?.settings?.tableService ?? 'off') === 'off') return false;
    return !this._data.settings.orderTypeEnabled || this._orderType === 'dine-in';
  }

  _tablePickerMarkup() {
    if (this._tabPay || (this._data?.settings?.tableService ?? 'off') === 'off') return '';
    const picked = this._table;
    const chips = this._tables.map((t) => {
      const on = picked?.id === t.id;
      return `<button type="button" class="co-table-chip${on ? ' co-table-chip--on' : ''}${t.busy ? ' co-table-chip--busy' : ''}" data-table-id="${t.id}" aria-pressed="${on}">${esc(t.name)}</button>`;
    }).join('');
    const typed = picked && !picked.id ? picked.name : '';
    return `
      <div class="co-table${this._tablePickerShown() ? '' : ' hidden'}" id="coTablePicker">
        <div class="co-table-head"><span class="co-table-title">Which table?</span><span class="co-table-hint">So staff can bring it over</span></div>
        ${chips ? `<div class="co-table-chips" role="group" aria-label="Table">${chips}</div>` : ''}
        <div class="co-table-row">
          <input type="text" class="co-table-input" id="coTableInput" maxlength="12" placeholder="${chips ? 'Or type a table / tent #' : 'Table or tent #'}" value="${esc(typed)}" aria-label="Table or tent number" />
          <button type="button" class="co-table-skip${picked ? '' : ' co-table-skip--on'}" data-table-skip="1">No table</button>
        </div>
      </div>`;
  }

  _syncTablePicker() {
    const box = this._parentElement.querySelector('#coTablePicker');
    if (!box) return;
    box.classList.toggle('hidden', !this._tablePickerShown());
    box.querySelectorAll('.co-table-chip').forEach((b) => {
      const on = this._table?.id === b.dataset.tableId;
      b.classList.toggle('co-table-chip--on', on);
      b.setAttribute('aria-pressed', String(on));
    });
    box.querySelector('.co-table-skip')?.classList.toggle('co-table-skip--on', !this._table);
  }

  // The table this sale goes to, or null (none picked, takeout, or no tables).
  pickedTable() {
    return this._tablePickerShown() ? this._table : null;
  }

  _wireTablePicker() {
    this._parentElement.addEventListener('click', (e) => {
      const chip = e.target.closest('.co-table-chip');
      if (chip) {
        const t = this._tables.find((x) => x.id === chip.dataset.tableId);
        this._table = this._table?.id === t?.id ? null : (t ? { id: t.id, name: t.name } : null);
        const input = this._parentElement.querySelector('#coTableInput');
        if (input) input.value = '';
        this._syncTablePicker();
        return;
      }
      if (e.target.closest('[data-table-skip]')) {
        this._table = null;
        const input = this._parentElement.querySelector('#coTableInput');
        if (input) input.value = '';
        this._syncTablePicker();
      }
    });
    this._parentElement.addEventListener('input', (e) => {
      if (e.target.id !== 'coTableInput') return;
      const v = e.target.value.trim();
      this._table = v ? { name: v } : null;
      this._syncTablePicker();
    });
  }

  // ── Main markup ───────────────────────────────────────────────────────────────

  _generateMarkUp() {
    const allAdj = this._data.currentReceiptAdjustments ?? [];
    const adjResult = this._adjResult ?? {
      lineItems: [],
      finalTotal: this._totalPrice,
    };
    const showRemoved = this._data.settings?.showRemovedAdjustments ?? true;
    const subtotal = this._subtotal ?? this._totalPrice;
    const printingOn = this._data.settings?.printingEnabled ?? true;

    return `
<div class="pos-screen" id="newOrderModal">
  <div class="pos-header">
    <button class="checkout-back-btn pos-back-btn" type="button" title="Back to order">
      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
        <polyline points="15 18 9 12 15 6"/>
      </svg>
      Back
    </button>
    <h2 class="pos-title">Checkout</h2>
    <button class="modal-close pos-close-btn" aria-label="Close">&times;</button>
  </div>

  <div class="pos-checkout-body">
    <div class="pos-checkout-cart">
      <div class="co-receipt" id="coReceipt">
        <div class="co-receipt-head" id="coReceiptHead">
          <h3 class="pos-checkout-section-title">Order Summary</h3>
          <span class="co-receipt-count" id="coReceiptCount">${this._cartSummaryMeta(this._data.cart).countText}</span>
          <!-- Phone only: the summary folds to this one line; tap to show the items. -->
          <b class="co-receipt-head-sub" id="coReceiptHeadSub">${this._cartSummaryMeta(this._data.cart).subtotalText}</b>
          <button type="button" class="co-receipt-toggle" id="coReceiptToggle" aria-expanded="false" aria-controls="cartItems" aria-label="Show items">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m6 9 6 6 6-6"/></svg>
          </button>
        </div>
        <div id="cartItems" class="pos-checkout-cart-list">
          ${this._generateCartItemsMarkup(this._data.cart)}
        </div>
        <div class="co-receipt-foot">
          <span>Items subtotal</span>
          <b id="coReceiptSubtotal">${this._cartSummaryMeta(this._data.cart).subtotalText}</b>
        </div>
      </div>
      ${this._generateExtrasMarkup(adjResult)}
    </div>

    <div class="pos-checkout-payment">
      ${this._tabPay ? `<div class="co-tab-pay"><span class="co-tab-pay-badge">${esc(this._tabPay.tableName ?? '')}</span><span>${this._tabPay.split ? 'Paying part of the tab' : 'Paying the tab'}</span></div>` : ''}
      ${!this._tabPay && this._data.settings.orderTypeEnabled ? `<div class="order-type-toggle">
        <button class="order-type-btn${this._orderType === 'dine-in' ? ' order-type-btn--active' : ''}" data-order-type="dine-in" type="button"><span class="pos-btn-emoji" aria-hidden="true">🍽️</span>Dine In</button>
        <button class="order-type-btn${this._orderType === 'takeout' ? ' order-type-btn--active' : ''}" data-order-type="takeout" type="button"><span class="pos-btn-emoji" aria-hidden="true">🥡</span>${takeoutLabel()}</button>
      </div>` : ''}
      ${this._tablePickerMarkup()}
      <div id="receiptAdjSection">
        ${this._generateBillMarkup(subtotal, allAdj, adjResult, showRemoved)}
      </div>

      <div class="cart-total pos-checkout-total">
        <span class="pos-total-label"><span class="pos-section-emoji" aria-hidden="true">🛒</span>Total</span>
        <span class="pos-total-amount" id="cartTotal">${formatMoney(this.chargeTotal())}</span>
      </div>
      <p class="co-tip-split" id="coTipSplit">${this._tipSplitText()}</p>
      <p class="pos-tax-incl-line" id="taxInclLine">${this._taxInclLineHtml()}</p>

      <div class="pos-pay-panel">
        <div class="pos-section-head"><span class="pos-section-emoji" aria-hidden="true">💳</span>Payment Method</div>
        <div class="payment-method-toggle" id="paymentMethodToggle">
          <button class="payment-method-btn${this._paymentMethod === 'cash' ? ' payment-method-btn--active' : ''}" data-payment-method="cash" type="button"><span class="pos-btn-emoji" aria-hidden="true">💵</span>Cash</button>
          <button class="payment-method-btn${this._paymentMethod === 'card' ? ' payment-method-btn--active' : ''}" data-payment-method="card" type="button"><span class="pos-btn-emoji" aria-hidden="true">💳</span>Card</button>
          <button class="payment-method-btn${this._paymentMethod === 'qr' ? ' payment-method-btn--active' : ''}" data-payment-method="qr" type="button"><span class="pos-btn-emoji" aria-hidden="true">📱</span>QR</button>
        </div>

        <div id="cashPaymentFields" class="pos-cash-fields${this._paymentMethod === 'cash' ? '' : ' hidden'}">
          <div class="receive-container">
            <label for="customerPayment">Amount Received</label>
            <div class="pos-payment-row">
              <div class="pos-amount-input">
                <span class="pos-amount-currency" aria-hidden="true">${currencySymbol()}</span>
                <input type="number" id="customerPayment" placeholder="Enter amount received" min="0" step="0.01" />
              </div>
              <button id="enterPaymentBtn">Enter</button>
            </div>
          </div>

          <div class="pos-change-row">
            <span class="pos-change-caption"><span class="pos-section-emoji" aria-hidden="true">💵</span>Change Due</span>
            <div id="changeAmount" class="change-box">${formatMoney(0)}</div>
          </div>
        </div>

        <p class="payment-paid-note hidden" id="paidInFullNote">Marked as paid in full · no change due</p>
      </div>

      <p class="pos-tip${this._paymentMethod === 'card' ? ' hidden' : ''}" id="paymentTip"><span class="pos-tip-emoji" aria-hidden="true">💡</span><span id="paymentTipText">${this._tipText(this._paymentMethod)}</span></p>

      <!-- On phones this bar pins to the bottom with the total, so the sale can
           always be finished without scrolling. On wider screens it's just the button. -->
      <div class="co-paybar">
        <div class="co-paybar-total"><span>Total</span><b id="coPaybarTotal">${formatMoney(this.chargeTotal())}</b></div>
        <button class="print-receipt-btn${printingOn ? '' : ' print-receipt-btn--off'} hidden" id="printReceiptBtn">
          ${printingOn ? 'Print Receipt' : 'Record Sale<span class="print-off-note">Printing is off · Enable in Settings</span>'}
        </button>
      </div>
    </div>
  </div>
</div>
    `;
  }

  // ── In-place refresh (does not reset the payment input) ───────────────────────

  _refreshAdjustments(subtotal, allAdj, adjResult, showRemoved, promoCode = null, loyalty = null) {
    this._subtotal = subtotal;
    this._adjResult = adjResult;
    this._totalPrice = adjResult.finalTotal;
    this._promoCode = promoCode;
    this._loyalty = loyalty;

    const section = this._parentElement.querySelector("#receiptAdjSection");
    if (section) section.innerHTML = this._generateBillMarkup(subtotal, allAdj, adjResult, showRemoved);
    const promoPane = this._parentElement.querySelector("#coPromoPane");
    if (promoPane) promoPane.innerHTML = this._generateDiscountPaneMarkup(promoCode, adjResult);
    const loyaltyPane = this._parentElement.querySelector("#coLoyaltyPane");
    if (loyaltyPane) loyaltyPane.innerHTML = this._generateLoyaltySection(loyalty, adjResult);

    const taxLineEl = this._parentElement.querySelector("#taxInclLine");
    if (taxLineEl) taxLineEl.innerHTML = this._taxInclLineHtml();

    // Total + a percentage tip follow the new sale total; resets payment.
    this.refreshTip();
  }

  // Total (or tip) changed — reset payment validation state
  _resetPaymentAfterTotalChange() {
    const changeBox = this._parentElement.querySelector("#changeAmount");
    if (changeBox) {
      changeBox.textContent = formatMoney(0);
      changeBox.classList.remove("ok", "change-box--error");
    }
    this._parentElement
      .querySelector(".print-receipt-btn")
      ?.classList.add("hidden");
    this._customerChange = undefined;

    // Non-cash methods are paid-in-full at the new total, so re-stamp and
    // re-enable the record button (cash still requires a fresh amount entry).
    if (this._paymentMethod && this._paymentMethod !== "cash") {
      this._applyPaymentMethodUI(this._paymentMethod);
    }
  }

  // ── Inline forms ──────────────────────────────────────────────────────────────

  _showReceiptEditForm(adj) {
    this._removeReceiptForms();
    const item = this._parentElement.querySelector(
      `.receipt-adj-item[data-adj-id="${adj.id}"]`,
    );
    if (!item) return;

    const html = `
      <div class="receipt-edit-form" id="receiptEditForm">
        <label class="adj-form-sublabel">
          New value (${adj.calculation === "percentage" ? "%" : currencySymbol()})
        </label>
        <input type="number" id="receiptEditValue" value="${adj.appliedValue}" min="0" step="0.01" />
        <div class="adj-form-actions" style="margin-top:6px;">
          <button class="btn" id="receiptEditCancelBtn" type="button">Cancel</button>
          <button class="btn primary" id="receiptEditSaveBtn" data-adj-id="${adj.id}" type="button">Save</button>
        </div>
      </div>
    `;
    item.insertAdjacentHTML("afterend", html);
    document
      .getElementById("receiptEditCancelBtn")
      .addEventListener("click", () => this._removeReceiptForms());
    document.getElementById("receiptEditValue").focus();
  }

  _showReceiptAddManualForm() {
    this._removeReceiptForms();
    const modal = this._parentElement.querySelector(".pos-screen");
    if (!modal) return;

    const html = `
      <div class="receipt-manual-overlay" id="receiptManualForm">
      <div class="adj-form receipt-manual-card">
        <h4 class="adj-form-title">Add One-off Adjustment</h4>

        <div class="edit-field">
          <label for="receiptManualName">Name</label>
          <input type="text" id="receiptManualName" placeholder="e.g. Discount, Delivery fee" />
        </div>

        <p class="adj-form-sublabel">Type</p>
        <div class="adj-selector" id="receiptManualTypeSelector">
          <button type="button" class="adj-selector-btn active" data-value="fee">Fee</button>
          <button type="button" class="adj-selector-btn" data-value="discount">Discount</button>
        </div>
        <input type="hidden" id="receiptManualType" value="fee" />

        <p class="adj-form-sublabel">Calculation</p>
        <div class="adj-selector" id="receiptManualCalcSelector">
          <button type="button" class="adj-selector-btn active" data-value="fixed">Fixed ($)</button>
          <button type="button" class="adj-selector-btn" data-value="percentage">
            Percentage (%)
            <span class="pb-tip" tabindex="0" role="img" aria-label="More info" data-tip="% of the running subtotal at the time this adjustment is applied">i</span>
          </button>
        </div>
        <input type="hidden" id="receiptManualCalc" value="fixed" />

        <div class="edit-field">
          <label id="receiptManualValueLabel">Value ($)</label>
          <input type="number" id="receiptManualValue" min="0" step="0.01" placeholder="0" />
        </div>

        <div class="adj-form-actions">
          <button type="button" class="btn" id="receiptManualCancelBtn">Cancel</button>
          <button type="button" class="btn primary" id="receiptManualSaveBtn">Add</button>
        </div>
      </div>
      </div>
    `;
    modal.insertAdjacentHTML("beforeend", html);

    document
      .querySelectorAll("#receiptManualTypeSelector, #receiptManualCalcSelector")
      .forEach((group) => {
        group.addEventListener("click", (e) => {
          const btn = e.target.closest(".adj-selector-btn");
          if (!btn) return;
          group
            .querySelectorAll(".adj-selector-btn")
            .forEach((b) => b.classList.remove("active"));
          btn.classList.add("active");
          if (group.id === "receiptManualCalcSelector") {
            document.getElementById("receiptManualCalc").value = btn.dataset.value;
            document.getElementById("receiptManualValueLabel").textContent =
              btn.dataset.value === "percentage" ? "Value (%)" : "Value ($)";
          } else {
            document.getElementById("receiptManualType").value = btn.dataset.value;
          }
        });
      });

    document
      .getElementById("receiptManualCancelBtn")
      .addEventListener("click", () => this._removeReceiptForms());
    document.getElementById("receiptManualName").focus();
  }

  _removeReceiptForms() {
    document.getElementById("receiptEditForm")?.remove();
    document.getElementById("receiptManualForm")?.remove();
  }

  // ── Handlers ──────────────────────────────────────────────────────────────────

  // Discount / Loyalty / Tip tabs, and the phone-only fold on the order summary.
  // Both switch in place, so an amount already typed into the payment stays put.
  _wireExtras() {
    this._parentElement.addEventListener('click', (e) => {
      const tab = e.target.closest('.co-extras-tab');
      if (tab) { this._selectExtrasTab(tab.dataset.extrasTab); return; }
      const head = e.target.closest('#coReceiptHead');
      if (!head || !window.matchMedia('(max-width: 768px)').matches) return;
      const receipt = this._parentElement.querySelector('#coReceipt');
      const open = !receipt.classList.contains('co-receipt--open');
      receipt.classList.toggle('co-receipt--open', open);
      const toggle = this._parentElement.querySelector('#coReceiptToggle');
      toggle?.setAttribute('aria-expanded', String(open));
      toggle?.setAttribute('aria-label', open ? 'Hide items' : 'Show items');
    });
  }

  _wireOrderType() {
    this._parentElement.addEventListener('click', (e) => {
      const btn = e.target.closest('.order-type-btn');
      if (!btn) return;
      this._orderType = btn.dataset.orderType;
      this._parentElement.querySelectorAll('.order-type-btn').forEach(b =>
        b.classList.toggle('order-type-btn--active', b === btn)
      );
      this._syncTablePicker();
    });
    this._wireTablePicker();
  }

  // Small "Includes VAT (12%) $X" note under the total when the business runs
  // tax-inclusive pricing. Empty string (hidden via :empty) otherwise.
  //
  // Per-item exemption (2026-07-07): cart items aren't stamped with a tax class
  // yet (that happens at _buildSale, once the sale is recorded) - render(modelState)
  // already hands the WHOLE model state to this view as this._data, so
  // this._data.menuItems is available here without calling model.* directly
  // (views never call model functions - CLAUDE.md). The cart's taxable/exempt
  // RATIO is applied to this._totalPrice (which may include on-top adjustments)
  // so a cart with no exempt items produces the exact same output as before
  // (ratio = 1) - only a mixed cart's line actually changes.
  _taxInclLineHtml() {
    const s = this._data?.settings;
    if (!s?.taxInclusive) return '';
    const cart = this._data?.cart ?? [];
    const menuItems = this._data?.menuItems ?? [];
    const isExempt = (it) => menuItems.find((m) => m._id === it.id)?.taxClass === 'exempt';
    const { taxableGross: cartTaxable, exemptGross: cartExempt } = splitTaxableGross(cart, isExempt);
    const cartTotal = cartTaxable + cartExempt;
    if (cartTotal <= 0) return '';
    const ratio = cartTaxable / cartTotal;
    const taxablePortion = this._totalPrice * ratio;
    const exemptPortion = this._totalPrice - taxablePortion;
    const vat = extractInclusiveTax(taxablePortion, s.taxInclusiveRate);
    const vatLine = vat ? `Includes ${esc(taxLabel())} (${vat.rate}%) ${formatMoney(vat.tax)}` : '';
    const exemptLine = cartExempt > 0 ? `${exemptLabel()} items: ${formatMoney(exemptPortion)} (not taxed)` : '';
    return [vatLine, exemptLine].filter(Boolean).join('<br>');
  }

  // Helper text under the payment panel. Cash explains the change math; QR tells
  // the cashier the merchant QR is now up on the customer display; Card shows none.
  _tipText(method) {
    if (method === 'qr')
      return 'The payment QR is now showing on the customer display for the customer to scan.';
    return 'Enter the amount the customer gives you to automatically calculate the change.';
  }

  // Cash keeps the amount-received → change flow. Card / QR have no change, so
  // we hide that step, stamp the exact total as paid in full, and let the cashier
  // record immediately. Called both on a button click and after the total changes.
  _applyPaymentMethodUI(method) {
    this._paymentMethod = method;
    this._parentElement.querySelectorAll('.payment-method-btn').forEach(b =>
      b.classList.toggle('payment-method-btn--active', b.dataset.paymentMethod === method)
    );

    const cashFields = this._parentElement.querySelector('#cashPaymentFields');
    const paidNote   = this._parentElement.querySelector('#paidInFullNote');
    const recordBtn  = this._parentElement.querySelector('.print-receipt-btn');
    const changeBox  = this._parentElement.querySelector('#changeAmount');
    const tip        = this._parentElement.querySelector('#paymentTip');
    const tipText    = this._parentElement.querySelector('#paymentTipText');

    // Tip shows for cash + qr (each with its own copy), hidden for card.
    if (tipText) tipText.textContent = this._tipText(method);
    tip?.classList.toggle('hidden', method === 'card');

    if (method === 'cash') {
      cashFields?.classList.remove('hidden');
      paidNote?.classList.add('hidden');
      // Require the cashier to enter the amount received before recording.
      this._customerPayment = undefined;
      this._customerChange = undefined;
      if (changeBox) { changeBox.textContent = formatMoney(0); changeBox.classList.remove('ok', 'change-box--error'); }
      recordBtn?.classList.add('hidden');
    } else {
      // Card / QR: exact total, no change.
      cashFields?.classList.add('hidden');
      paidNote?.classList.remove('hidden');
      this._customerPayment = this.chargeTotal();
      this._customerChange = 0;
      recordBtn?.classList.remove('hidden');
    }
  }

  _addHandlerPaymentMethod(handler) {
    this._parentElement.addEventListener('click', (e) => {
      const btn = e.target.closest('.payment-method-btn');
      if (!btn) return;
      const method = btn.dataset.paymentMethod;
      this._applyPaymentMethodUI(method);
      handler(method);
    });
  }

  _addHandlerShowCheckout(handler) {
    this._parentElement.addEventListener("click", function (e) {
      const btn = e.target.closest(".btn-checkout");
      if (!btn) return;
      handler();
    });
  }

  _subtractChange() {
    // Block -, +, e, E from being typed into the payment field
    this._parentElement.addEventListener("keydown", (e) => {
      if (e.target.id !== "customerPayment") return;
      if (["-", "+", "e", "E"].includes(e.key)) e.preventDefault();
    });

    this._parentElement.addEventListener("click", (e) => {
      const btn = e.target.closest("#enterPaymentBtn");
      if (!btn) return;

      const changeBox = document.querySelector(".change-box");
      const raw = this._parentElement.querySelector("#customerPayment").value;
      const payment = parseFloat(raw);

      if (!raw || isNaN(payment) || payment <= 0) {
        changeBox.classList.remove("ok");
        changeBox.classList.add("change-box--error");
        changeBox.textContent = "Enter a valid payment amount";
        return;
      }

      this._customerPayment = payment;

      const due = this.chargeTotal();
      if (payment < due) {
        changeBox.classList.remove("ok");
        changeBox.classList.add("change-box--error");
        changeBox.textContent = this.tipAmount() > 0
          ? `Payment must be at least the total with tip`
          : `Payment must be at least the order total`;
      } else {
        const change = payment - due;
        changeBox.classList.add("ok");
        changeBox.classList.remove("change-box--error");
        changeBox.textContent = formatMoney(change);
        this._customerChange = change;
        document.querySelector(".print-receipt-btn").classList.toggle("hidden");
      }
    });
  }

  _addHandlerPrintReceipt(handler) {
    this._parentElement.addEventListener("click", function (e) {
      const btn = e.target.closest("#printReceiptBtn");
      if (!btn) return;
      handler();
    });
  }

  _addHandlerReceiptEdit(handler) {
    this._parentElement.addEventListener("click", (e) => {
      const btn = e.target.closest(".receipt-adj-edit-btn");
      if (!btn) return;
      handler(btn.dataset.adjId);
    });
  }

  _addHandlerReceiptRemove(handler) {
    this._parentElement.addEventListener("click", (e) => {
      const btn = e.target.closest(".receipt-adj-remove-btn");
      if (!btn) return;
      handler(btn.dataset.adjId);
    });
  }

  _addHandlerReceiptAddManual(handler) {
    this._parentElement.addEventListener("click", (e) => {
      if (!e.target.closest(".receipt-add-adj-btn")) return;
      handler();
    });
  }

  _addHandlerReceiptSaveOverride(handler) {
    this._parentElement.addEventListener("click", (e) => {
      const btn = e.target.closest("#receiptEditSaveBtn");
      if (!btn) return;
      const value = parseFloat(
        document.getElementById("receiptEditValue").value,
      );
      if (isNaN(value) || value < 0) {
        alert("Please enter a valid value.");
        return;
      }
      handler({ id: btn.dataset.adjId, value });
    });
  }

  _addHandlerReceiptSaveManual(handler) {
    this._parentElement.addEventListener("click", (e) => {
      if (!e.target.closest("#receiptManualSaveBtn")) return;
      const name = document.getElementById("receiptManualName")?.value.trim();
      const type = document.getElementById("receiptManualType")?.value;
      const calculation = document.getElementById("receiptManualCalc")?.value;
      const value = parseFloat(
        document.getElementById("receiptManualValue")?.value,
      );
      if (!name) {
        alert("Please enter a name.");
        return;
      }
      if (isNaN(value) || value < 0) {
        alert("Please enter a valid value.");
        return;
      }
      handler({ name, type, calculation, value });
      this._removeReceiptForms();
    });
  }

  _addHandlerApplyPromo(handler) {
    this._parentElement.addEventListener("click", (e) => {
      if (!e.target.closest("#applyPromoBtn")) return;
      const input = this._parentElement.querySelector("#promoCodeInput");
      const code = input?.value.trim();
      if (!code) return;
      handler(code);
    });
  }

  _addHandlerRemovePromo(handler) {
    this._parentElement.addEventListener("click", (e) => {
      if (!e.target.closest("#removePromoBtn")) return;
      handler();
    });
  }

  // ── Loyalty handlers (all delegated on .modal-parent, like the promo ones) ───

  _addHandlerLoyaltyLookup(handler) {
    this._parentElement.addEventListener("click", (e) => {
      if (!e.target.closest("#loyaltyLookupBtn")) return;
      const phone = this._parentElement.querySelector("#loyaltyPhoneInput")?.value.trim();
      if (!phone) return;
      handler(phone);
    });
    // Enter in the phone input triggers the lookup too.
    this._parentElement.addEventListener("keydown", (e) => {
      if (e.key !== "Enter" || e.target.id !== "loyaltyPhoneInput") return;
      e.preventDefault();
      const phone = e.target.value.trim();
      if (phone) handler(phone);
    });
  }

  _addHandlerLoyaltyEnroll(handler) {
    this._parentElement.addEventListener("click", (e) => {
      if (!e.target.closest("#loyaltyEnrollBtn")) return;
      const phone = this._parentElement.querySelector("#loyaltyEnrollPhone")?.value.trim();
      const name = this._parentElement.querySelector("#loyaltyEnrollName")?.value.trim();
      if (!phone) return;
      handler(phone, name);
    });
  }

  _addHandlerLoyaltyEnrollCancel(handler) {
    this._parentElement.addEventListener("click", (e) => {
      if (!e.target.closest("#loyaltyEnrollCancelBtn")) return;
      handler();
    });
  }

  // Live character filtering for the checkout loyalty fields (delegated, so it
  // survives every section re-render): phone = digits only, enroll name =
  // letters/spaces/'-. only. Mirrors the panel form's sanitizers.
  _wireLoyaltyInputs() {
    this._parentElement.addEventListener("input", (e) => {
      const el = e.target;
      const strip =
        el.id === "loyaltyPhoneInput" ? stripNonDigits :
        el.id === "loyaltyEnrollName" ? stripNonNameChars : null;
      if (!strip) return;
      const caretKept = strip(
        el.value.slice(0, el.selectionStart ?? el.value.length),
      ).length;
      const clean = strip(el.value);
      if (clean !== el.value) {
        el.value = clean;
        try { el.setSelectionRange(caretKept, caretKept); } catch {}
      }
    });
  }

  _addHandlerLoyaltyRedeemOpen(handler) {
    this._parentElement.addEventListener("click", (e) => {
      if (!e.target.closest("#loyaltyRedeemOpenBtn")) return;
      handler();
    });
  }

  // Stepper +/- are a pure view concern: adjust the count within [1, max] and
  // update the live "pts = −$X" preview. Only Redeem hands off to the controller.
  // Delegated on document (not .modal-parent) because the stepper now only
  // ever lives inside the top-level Redeem modal, which sits outside .modal-parent.
  _wireLoyaltyStepper() {
    document.addEventListener("click", (e) => {
      const btn = e.target.closest("#loyaltyRedeemMinus, #loyaltyRedeemPlus");
      if (!btn) return;
      const card = btn.closest(".loyalty-points-off-card");
      const stepper = card?.querySelector(".loyalty-stepper");
      const countEl = card?.querySelector("#loyaltyRedeemCount");
      if (!stepper || !countEl) return;
      const max = parseInt(stepper.dataset.max) || 1;
      const delta = btn.id === "loyaltyRedeemPlus" ? 1 : -1;
      const next = Math.min(max, Math.max(1, (parseInt(countEl.textContent) || 1) + delta));
      countEl.textContent = String(next);
      const pts = next * (parseInt(card.dataset.redeemPoints) || 0);
      const val = next * (parseFloat(card.dataset.redeemValue) || 0);
      const ptsEl = card.querySelector(".loyalty-points-off-pts");
      const valEl = card.querySelector(".loyalty-points-off-value");
      if (ptsEl) ptsEl.textContent = `${pts} pts`;
      if (valEl) valEl.textContent = `−${formatMoney(val)}`;
    });
  }

  _addHandlerLoyaltyRedeem(handler) {
    document.addEventListener("click", (e) => {
      if (!e.target.closest("#loyaltyApplyRedeemBtn")) return;
      const count = parseInt(
        document.querySelector("#loyaltyRedeemCount")?.textContent,
      ) || 1;
      handler(count);
    });
  }

  // .loyalty-remove-redeem-btn can appear in the checkout section's applied-list
  // summary AND inside the Redeem modal at once - a class (not an id, which
  // would duplicate in the DOM) delegated on document to cover both.
  _addHandlerLoyaltyRemoveRedeem(handler) {
    document.addEventListener("click", (e) => {
      if (!e.target.closest(".loyalty-remove-redeem-btn")) return;
      handler();
    });
  }

  _addHandlerLoyaltyUnlink(handler) {
    this._parentElement.addEventListener("click", (e) => {
      if (!e.target.closest("#loyaltyUnlinkBtn")) return;
      handler();
    });
  }

  _addHandlerBack(handler) {
    this._parentElement.addEventListener("click", (e) => {
      if (!e.target.closest(".checkout-back-btn")) return;
      handler();
    });
  }

  _addHandlerDeleteCartItem(handler) {
    this._parentElement.addEventListener("click", (e) => {
      const btn = e.target.closest(".checkout-cart-delete-btn");
      if (!btn) return;
      handler(Number(btn.dataset.cartIndex));
    });
  }

  // Only ever rendered inside the Redeem modal now (outside .modal-parent).
  _addHandlerLoyaltyRewardRedeem(handler) {
    document.addEventListener("click", (e) => {
      const btn = e.target.closest(".loyalty-reward-redeem-btn");
      if (!btn) return;
      handler(btn.dataset.rewardId);
    });
  }

  // .loyalty-reward-remove-btn appears both in the section's applied-list
  // summary AND inside the Redeem modal - document-delegated to cover both.
  _addHandlerLoyaltyRewardRemove(handler) {
    document.addEventListener("click", (e) => {
      const btn = e.target.closest(".loyalty-reward-remove-btn");
      if (!btn) return;
      handler(btn.dataset.cartLineId);
    });
  }

  _hideModal() {
    this._parentElement.innerHTML = "";
  }

  // Pre-commit receipt preview: shows the exact receipt (in an iframe) that will
  // print, with Go Back / Confirm. Nothing is recorded until Confirm. Reuses the
  // `.confirm-overlay` shell so it stacks/behaves like the other confirm modals.
  showReceiptPreview({ html, confirmLabel = 'Confirm & Print', title = 'Record this sale?', note, onConfirm, onCancel }) {
    if (document.querySelector('.confirm-overlay')) return;
    const el = document.createElement('div');
    el.className = 'confirm-overlay receipt-preview-overlay';
    el.innerHTML = `
      <div class="confirm-card confirm-card--receipt">
        <p class="confirm-msg">${title}</p>
        <div class="receipt-preview-frame-wrap">
          <iframe class="receipt-preview-frame" title="Receipt preview"></iframe>
        </div>
        ${note ? `<p class="confirm-note">${note}</p>` : ''}
        <div class="confirm-actions">
          <button class="btn confirm-cancel-btn" type="button">Go Back</button>
          <button class="btn primary confirm-ok-btn" type="button">${confirmLabel}</button>
        </div>
      </div>
    `;
    document.body.appendChild(el);
    // Set srcdoc via property (avoids HTML-attribute escaping of the receipt markup).
    el.querySelector('.receipt-preview-frame').srcdoc = html;
    el.querySelector('.confirm-ok-btn').addEventListener('click', () => { el.remove(); onConfirm?.(); });
    el.querySelector('.confirm-cancel-btn').addEventListener('click', () => { el.remove(); onCancel?.(); });
    el.addEventListener('click', (e) => { if (e.target === el) { el.remove(); onCancel?.(); } });
  }
}

export default new OrderCheckOutView();
